import { bdTrackerAllowedRoles } from 'data/bdTracker/bdTrackerUserRoleValidations';
import { SiteTransferNotePayload } from 'Interfaces/BDPortalInterfaces';
import { observer } from 'mobx-react';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Dialog } from 'primereact/dialog';
import { Skeleton } from 'primereact/skeleton';
import ReactQuill from 'react-quill';
import { checkIfFunctionalityEnabled } from 'utils/facts/roleBasedFunctionalityAccess';
import { DateConversionUtility } from 'utils/dateUtilities';
import ConfirmationModal from '../../../../../components/ConfirmationModal/ConfirmationModal';
import SiteTransferLogic from './SiteTransfer.logic';
import styles from './SiteTransfer.module.scss';

const SiteTransferModal = ({
  visible,
  opportunityIterationID,
  closeModal,
  isIterationActive,
}: {
  visible: boolean;
  opportunityIterationID: number;
  closeModal: () => void;
  isIterationActive: boolean;
}) => {
  const {
    userModulesRoles,
    siteTransferLoading,
    siteTransferNotes,
    siteTransferNoteLoading,
    saveSiteTransferNote,
    setNote,
    note,
    handleNoteChange,
    siteTransfer,
    setSiteTransfer,
    handleSaveSiteTransfer,
    siteTransferSaving,
    validationError,
    setValidationError,
    isConfirmModalOpen,
    handleCloseSiteTransferModal,
    handleConfirmForConfirmationModal,
    handleCloseConfirmationModal,
  } = SiteTransferLogic(opportunityIterationID, closeModal);

  return (
    <Dialog
      contentClassName={styles.modalContent}
      header={'Site Transfer'}
      style={{ width: '40rem', height: '45rem', margin: 'auto' }}
      visible={visible}
      onHide={handleCloseSiteTransferModal}
      blockScroll
      draggable={false}
      resizable={false}
    >
      {!siteTransferLoading && siteTransfer ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            gap: '3rem',
            position: 'relative',
          }}
        >
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
            <div className={styles.dropdown}>
              <label htmlFor="negotiation">PROPOSAL SUBMITTED</label>
              <div className={styles.radioButtons} style={{ marginTop: '1rem' }}>
                <div className="p-field p-col-6">
                  <input
                    type="radio"
                    name="ProposalSubmitted"
                    value="Yes"
                    checked={siteTransfer?.ProposalSubmitted === true}
                    onChange={() =>
                      setSiteTransfer({
                        ...siteTransfer,
                        ProposalSubmitted: true,
                      })
                    }
                    disabled={!isIterationActive}
                  />
                  <label htmlFor="ProposalSubmitted-yes">Yes</label>
                </div>
                <div className="p-field p-col-6">
                  <input
                    type="radio"
                    name="ProposalSubmitted"
                    value="No"
                    checked={siteTransfer?.ProposalSubmitted === false}
                    onChange={() => {
                      setSiteTransfer({
                        ...siteTransfer,
                        ProposalSubmitted: false,
                        ProposalDate: null,
                      });
                      setValidationError(null);
                    }}
                    disabled={!isIterationActive}
                  />
                  <label htmlFor="ProposalSubmitted-no">No</label>
                </div>
              </div>
            </div>
            <div className={styles.dropdown}>
              <label htmlFor="negotiationsDate">
                SUBMISSION DATE
                {siteTransfer.ProposalSubmitted && <span style={{ color: 'red' }}>*</span>}
              </label>
              <Calendar
                view="date"
                dateFormat="mm/dd/yy"
                onChange={e => {
                  const formattedDate = DateConversionUtility.convertLocalToDBDateString(
                    e.value as Date
                  );
                  setSiteTransfer({
                    ...siteTransfer,
                    ProposalDate: formattedDate as unknown as Date,
                  });
                }}
                value={DateConversionUtility.convertDBDateToLocal(siteTransfer?.ProposalDate)}
                showIcon
                disabled={!siteTransfer?.ProposalSubmitted || !isIterationActive}
              />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
            <div className={styles.dropdown}>
              <label htmlFor="negotiation">LOI SUBMITTED</label>
              <div className={styles.radioButtons} style={{ marginTop: '1rem' }}>
                <div className="p-field p-col-6">
                  <input
                    type="radio"
                    name="LOISubmitted"
                    value="Yes"
                    checked={siteTransfer?.LOISubmitted === true}
                    onChange={() => setSiteTransfer({ ...siteTransfer, LOISubmitted: true })}
                    disabled={!isIterationActive}
                  />
                  <label htmlFor="LOISubmitted-yes">Yes</label>
                </div>
                <div className="p-field p-col-6">
                  <input
                    type="radio"
                    name="LOISubmitted"
                    value="No"
                    checked={siteTransfer?.LOISubmitted === false}
                    onChange={() => {
                      setSiteTransfer({
                        ...siteTransfer,
                        LOISubmitted: false,
                        LOIDate: null,
                      });
                      setValidationError(null);
                    }}
                    disabled={!isIterationActive}
                  />
                  <label htmlFor="LOISubmitted-no">No</label>
                </div>
              </div>
            </div>
            <div className={styles.dropdown}>
              <label htmlFor="negotiationsDate">
                SUBMISSION DATE
                {siteTransfer.LOISubmitted && <span style={{ color: 'red' }}>*</span>}
              </label>
              <Calendar
                view="date"
                dateFormat="mm/dd/yy"
                onChange={e => {
                  const formattedDate = DateConversionUtility.convertLocalToDBDateString(
                    e.value as Date
                  );
                  setSiteTransfer({
                    ...siteTransfer,
                    LOIDate: formattedDate as unknown as Date,
                  });
                }}
                value={DateConversionUtility.convertDBDateToLocal(siteTransfer?.LOIDate)}
                showIcon
                disabled={!siteTransfer?.LOISubmitted || !isIterationActive}
              />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
            <div className={styles.dropdown}>
              <label htmlFor="negotiation">LOI SIGNED</label>
              <div className={styles.radioButtons} style={{ marginTop: '1rem' }}>
                <div className="p-field p-col-6">
                  <input
                    type="radio"
                    name="LOISigned"
                    value="Yes"
                    checked={siteTransfer?.LOISigned === true}
                    onChange={() => setSiteTransfer({ ...siteTransfer, LOISigned: true })}
                    disabled={!isIterationActive}
                  />
                  <label htmlFor="LOISigned-yes">Yes</label>
                </div>
                <div className="p-field p-col-6">
                  <input
                    type="radio"
                    name="LOISigned"
                    value="No"
                    checked={siteTransfer?.LOISigned === false}
                    onChange={() => {
                      setSiteTransfer({
                        ...siteTransfer,
                        LOISigned: false,
                        LOISignedDate: null,
                      });
                      setValidationError(null);
                    }}
                    disabled={!isIterationActive}
                  />
                  <label htmlFor="LOISigned-no">No</label>
                </div>
              </div>
            </div>
            <div className={styles.dropdown}>
              <label htmlFor="negotiationsDate">
                SIGN DATE{siteTransfer.LOISigned && <span style={{ color: 'red' }}>*</span>}
              </label>
              <Calendar
                view="date"
                dateFormat="mm/dd/yy"
                onChange={e => {
                  const formattedDate = DateConversionUtility.convertLocalToDBDateString(
                    e.value as Date
                  );
                  setSiteTransfer({
                    ...siteTransfer,
                    LOISignedDate: formattedDate as unknown as Date,
                  });
                }}
                value={DateConversionUtility.convertDBDateToLocal(siteTransfer?.LOISignedDate)}
                showIcon
                disabled={!siteTransfer?.LOISigned || !isIterationActive}
              />
            </div>
          </div>
          <div
            style={{
              marginTop: '-2.5rem',
              marginBottom: '-2.5rem',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
            }}
          >
            {validationError &&
              validationError.map(item => (
                <span style={{ color: 'red', fontSize: '14px', fontWeight: '600' }}>
                  *{item.error}
                  <br />
                </span>
              ))}
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              width: '100%',
              marginBottom: '-2rem',
            }}
          >
            <Button
              onClick={handleSaveSiteTransfer}
              loading={siteTransferSaving}
              disabled={
                !checkIfFunctionalityEnabled(
                  bdTrackerAllowedRoles.siteTransferSaveButton,
                  userModulesRoles.opptracker,
                  userModulesRoles
                ) || !isIterationActive
              }
            >
              Save
            </Button>
          </div>
          <div style={{ paddingBottom: '2rem' }}>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <ReactQuill
                value={note || ''}
                onChange={content => handleNoteChange(content)}
                placeholder="Add site transfer notes"
                style={{
                  height: '5rem',
                  width: '100%',
                  pointerEvents: !isIterationActive ? 'none' : 'auto',
                }}
                modules={{
                  toolbar: [
                    ['bold', 'italic', 'underline'],
                    ['link'],
                    [{ list: 'ordered' }, { list: 'bullet' }],
                    ['clean'],
                  ],
                }}
              />
              <Button
                label="Add"
                style={{
                  backgroundColor: '#522D6E',
                  border: '1px solid #522D6E',
                  marginTop: '3rem',
                  alignSelf: 'flex-end',
                  padding: '0.5rem 0.75rem',
                }}
                disabled={
                  note === '' ||
                  !isIterationActive ||
                  !checkIfFunctionalityEnabled(
                    bdTrackerAllowedRoles.siteTransferSaveButton,
                    userModulesRoles.opptracker,
                    userModulesRoles
                  )
                }
                loading={siteTransferNoteLoading}
                onClick={() => {
                  if (siteTransfer?.OpportunityIterationID) {
                    const payload: SiteTransferNotePayload = {
                      OpportunityIterationID: siteTransfer.OpportunityIterationID,
                      Notes: note,
                    };
                    saveSiteTransferNote(payload);
                    setNote('');
                  }
                }}
              />
            </div>
            {siteTransferNotes?.map((note, index) => (
              <div
                key={index}
                style={{
                  paddingTop: '1rem',
                  borderBottom: '1px solid #00000030',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    gap: '0.75rem',
                    alignItems: 'center',
                  }}
                >
                  <h3 style={{ margin: 0 }}>{note.LastModifiedBy}</h3>
                </div>
                <div
                  style={{ margin: '1rem 0 1rem 1rem' }}
                  dangerouslySetInnerHTML={{ __html: note.Notes }}
                />
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              rowGap: '2rem',
              columnGap: '0.75rem',
              marginBottom: '2rem',
            }}
          >
            <Skeleton height="5rem" width="100%"></Skeleton>
            <Skeleton height="5rem" width="100%"></Skeleton>
            <Skeleton height="5rem" width="100%"></Skeleton>
            <Skeleton height="5rem" width="100%"></Skeleton>
            <Skeleton height="5rem" width="100%"></Skeleton>
            <Skeleton height="5rem" width="100%"></Skeleton>
          </div>
          <Skeleton height="10rem" width="100%"></Skeleton>
        </div>
      )}
      {isConfirmModalOpen && (
        <ConfirmationModal
          visibility={true}
          modalHeading="Confirmation"
          modalContent="Edits done will be lost, are you sure you want to continue ?"
          handleCancel={handleCloseConfirmationModal}
          handleConfirm={handleConfirmForConfirmationModal}
        />
      )}
    </Dialog>
  );
};

export default observer(SiteTransferModal);
